import { useUser } from "@clerk/tanstack-react-start";
import {
  challengeNumberToMapId,
  challengePublicTitle,
  currentChallengeNumber,
  isBrowsableChallengeLeaderboardNumber,
  isChallengeReplayPublic,
  parseChallengeNumber,
} from "@dailywars/core/engine";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useMemo } from "react";

import { PageEyeBrow } from "#/components/PageEyeBrow";
import { PageHeader } from "#/components/PageHeader";
import { PageIntro } from "#/components/PageIntro";
import { PageShell } from "#/components/PageShell";
import { Card, CardContent } from "#/components/ui/card";
import { Spinner } from "#/components/ui/spinner";
import {
  challengeLeaderboardDescription,
  challengeLeaderboardTitle,
  documentHead,
  mapOgImageUrl,
} from "#/lib/documentTitle";
import { mapLeaderboardInfiniteQueryOptions } from "#/lib/queries";
import { challengeNumberRouteParams } from "#/lib/routeParams";
import { loadChallengeLeaderboardPlayGate } from "#/server/game/challengeLeaderboardPlayGate";

import {
  ChallengeLeaderboardMapHero,
  LeaderboardEmptyBody,
  LeaderboardFilledBody,
} from "./-leaderboard.$mapId.ui";
import { ChallengeLeaderboardStatusNote } from "./-leaderboard.statusNote";

export const Route = createFileRoute("/challenge_/$challengeNumber_/leaderboard")({
  params: challengeNumberRouteParams,
  loader: async ({ context, params: { challengeNumber: challengeNumberParam } }) => {
    const challengeNumber = parseChallengeNumber(challengeNumberParam);
    if (challengeNumber === null || !isBrowsableChallengeLeaderboardNumber(challengeNumber)) {
      throw notFound();
    }
    const mapId = challengeNumberToMapId(challengeNumber);
    const isLive = challengeNumber === currentChallengeNumber();
    const [, playGate] = await Promise.all([
      context.queryClient.ensureInfiniteQueryData(mapLeaderboardInfiniteQueryOptions(mapId)),
      loadChallengeLeaderboardPlayGate({ data: { mapId } }),
    ]);
    return { mapId, challengeNumber, isLive, hasPlayed: playGate.hasPlayed };
  },
  head: ({ params: { challengeNumber: challengeNumberParam } }) => {
    const challengeNumber = parseChallengeNumber(challengeNumberParam);
    if (challengeNumber === null) return documentHead(challengeLeaderboardTitle(1));
    const mapId = challengeNumberToMapId(challengeNumber);
    return documentHead(challengeLeaderboardTitle(challengeNumber), {
      description: challengeLeaderboardDescription(challengeNumber),
      canonicalPath: `/challenge/${challengeNumber}/leaderboard`,
      imageUrl: mapOgImageUrl(mapId),
    });
  },
  component: ChallengeLeaderboardPage,
});

function ChallengeLeaderboardBoard({
  challengeNumber,
  viewerPlayerId,
  flatRows,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  loading,
}: {
  challengeNumber: number;
  viewerPlayerId: string | undefined;
  flatRows: Array<{
    playerId: string;
    displayName: string;
    score: number;
    dailyStreak: number;
    replayId?: string;
  }>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  loading: boolean;
}) {
  if (loading) {
    return (
      <CardContent className="flex items-center justify-center py-16">
        <Spinner />
      </CardContent>
    );
  }
  if (flatRows.length === 0) return <LeaderboardEmptyBody />;
  return (
    <LeaderboardFilledBody
      mapDate={String(challengeNumber)}
      viewerPlayerId={viewerPlayerId}
      flatRows={flatRows}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      variantLabel="Premium"
      showReplayLinks={isChallengeReplayPublic(challengeNumber)}
      medalsVisible={false}
      challengeStarsVisible
    />
  );
}

function ChallengeLeaderboardPage() {
  const { mapId, challengeNumber, isLive, hasPlayed } = Route.useLoaderData();
  const { user } = useUser();
  const viewerPlayerId = user?.id;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    mapLeaderboardInfiniteQueryOptions(mapId),
  );
  const flatRows = useMemo(() => data?.pages.flatMap((p) => p.entries) ?? [], [data]);
  const hidePlayCta =
    hasPlayed ||
    (viewerPlayerId !== undefined && flatRows.some((row) => row.playerId === viewerPlayerId));

  return (
    <PageShell>
      <div className="mx-auto flex w-full max-w-lg flex-col items-center gap-6">
        <PageIntro>
          <PageEyeBrow>Leaderboard</PageEyeBrow>
          <PageHeader>{challengePublicTitle(challengeNumber)}</PageHeader>
        </PageIntro>
        <ChallengeLeaderboardMapHero mapId={mapId} isLive={isLive} hidePlayCta={hidePlayCta} />
        <ChallengeLeaderboardStatusNote isLive={isLive} />
        <Card className="w-full overflow-hidden">
          <ChallengeLeaderboardBoard
            challengeNumber={challengeNumber}
            viewerPlayerId={viewerPlayerId}
            flatRows={flatRows}
            hasNextPage={hasNextPage ?? false}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            loading={data === undefined}
          />
        </Card>
      </div>
    </PageShell>
  );
}
